import {Text} from '@dropbox/dig-components/typography';
import {Box} from '@dropbox/dig-foundations';
import {AiQueryCapabilityResult} from 'views/admin/adminAiQueryApi';
import {getProjectionForCapability} from 'views/admin/aiQueryColumns';
import {buildCsv, csvFilename, downloadCsv} from 'views/admin/aiQueryCsv';
import {ResultDataset} from 'views/admin/RecipeReplay';

/** `include_full_body` / `workstream.name` -> `Include Full Body` / `Workstream Name`. */
const humanizeArgKey = (key: string): string =>
  key
    .replace(/[_.]/g, ' ')
    .trim()
    .replace(/\b\w/g, (c) => c.toUpperCase());

const humanizeArgScalar = (value: unknown): string | null => {
  if (value == null || value === '') {
    return null;
  }
  if (typeof value === 'number') {
    return value.toLocaleString();
  }
  if (typeof value === 'string') {
    return value.replace(/_/g, ' ');
  }
  return String(value);
};

const humanizeArgValue = (value: unknown): string | null => {
  if (Array.isArray(value)) {
    const parts = value.map(humanizeArgValue).filter((p): p is string => Boolean(p));
    return parts.length > 0 ? parts.join(', ') : null;
  }
  if (value !== null && typeof value === 'object') {
    const parts = Object.entries(value as Record<string, unknown>)
      .map(([k, v]) => {
        const formatted = humanizeArgValue(v);
        return formatted ? `${humanizeArgKey(k)}: ${formatted}` : null;
      })
      .filter((p): p is string => Boolean(p));
    return parts.length > 0 ? parts.join(', ') : null;
  }
  return humanizeArgScalar(value);
};

/**
 * Turn a tool call's args into short readable labels for pill display, e.g.
 * `{order: 'desc', limit: 3, include_full_body: true}` -> ["Order: desc",
 * "Limit: 3", "Full text"]. Falsey boolean flags are dropped (their absence
 * reads as "off") so the row stays terse.
 */
export const humanizeArgEntries = (args: Record<string, unknown> | undefined): string[] => {
  if (!args) {
    return [];
  }
  return Object.entries(args).flatMap(([key, value]) => {
    const label = humanizeArgKey(key);
    if (typeof value === 'boolean') {
      return value ? [label] : [];
    }
    const formatted = humanizeArgValue(value);
    return formatted == null ? [] : [`${label}: ${formatted}`];
  });
};

/**
 * Humanized args shown as a single subtle subtitle line beneath a step's title
 * (e.g. "Role: dri · Group By: person · Limit: 10"), replacing the args JSON.
 */
export const StepArgs = ({args}: {args: Record<string, unknown> | undefined}) => {
  const entries = humanizeArgEntries(args);
  if (entries.length === 0) {
    return null;
  }
  return (
    <Text color="subtle" size="xsmall">
      {entries.join(' · ')}
    </Text>
  );
};

/**
 * The rows a tool call returned, rendered with the same projection-driven table
 * used for saved views (rich person/workstream/status cells) instead of raw
 * JSON. The trace sample is already capped server-side, so no preview cap note.
 */
export const StepResponse = ({
  toolName,
  items,
  totalFound,
  truncated,
  previewRows,
}: {
  toolName: string;
  items: Array<Record<string, unknown>>;
  totalFound: number | null;
  truncated: boolean;
  /** How many rows to show; the rest are in the step's CSV download. */
  previewRows?: number;
}) => {
  const result: AiQueryCapabilityResult = {
    capability: toolName,
    items,
    total_found: totalFound,
    truncated,
  };
  return (
    <Box paddingTop="4" style={{width: '100%', overflowX: 'auto'}}>
      <ResultDataset result={result} showDownload={false} previewRows={previewRows} />
    </Box>
  );
};

/**
 * Download a single trace step's rows as CSV, using the same projection the
 * table uses. Lets users grab an intermediate dataset; the answer's top-level
 * CSV chip stays scoped to the final result the user asked for. Returns null
 * when the step has no renderable columns (nothing useful to export).
 */
export const buildStepCsvDownload = (
  toolName: string,
  items: Array<Record<string, unknown>>
): (() => void) | undefined => {
  if (items.length === 0) {
    return undefined;
  }
  const projection = getProjectionForCapability(toolName, items);
  if (projection.columns.length === 0) {
    return undefined;
  }
  return () => downloadCsv(buildCsv(items, projection), csvFilename(projection));
};
